/**
 * IngredientsSlide - "What's Inside" gallery slide
 * Shows the 5 key active ingredients in a clean, premium square layout
 */

export default function IngredientsSlide() {
  const ingredients = [
    {
      name: "Acetyl Tetrapeptide-3",
      shortName: "AHK-Cu",
      benefit: "Anchors hair at the root",
      dose: "3%",
      color: "#0066CC",
    },
    {
      name: "Copper Tripeptide-1",
      shortName: "GHK-Cu",
      benefit: "Reactivates dormant follicles",
      dose: "2%",
      color: "#0052A3",
    },
    {
      name: "Saw Palmetto Extract",
      shortName: "SPE-95",
      benefit: "Blocks DHT naturally",
      dose: "5%",
      color: "#16a34a",
    },
    {
      name: "Caffeine Complex",
      shortName: "CAF-X",
      benefit: "Stimulates scalp circulation",
      dose: "4%",
      color: "#7c3aed",
    },
    {
      name: "Niacinamide B3",
      shortName: "NAM-B3",
      benefit: "Strengthens hair shaft",
      dose: "3%",
      color: "#dc2626",
    },
  ];

  return (
    <div className="w-full h-full flex flex-col bg-[#f8f9fb] relative overflow-hidden select-none">
      {/* Header */}
      <div className="pt-5 pb-3 px-5 lg:pt-6 lg:pb-4 lg:px-7">
        <p className="text-[10px] lg:text-xs font-semibold tracking-[0.2em] text-[#0066CC] uppercase">
          Clinical-Grade Formula
        </p>
        <h3 className="text-base lg:text-xl font-bold text-gray-900 mt-1 font-heading uppercase tracking-tight">
          What's Inside
        </h3>
      </div>

      {/* Ingredients grid */}
      <div className="flex-1 px-4 pb-4 lg:px-6 lg:pb-5 flex flex-col gap-2 lg:gap-2.5">
        {ingredients.map((ing, i) => (
          <div
            key={i}
            className="flex items-center gap-3 lg:gap-4 bg-white rounded-xl px-3 py-2.5 lg:px-4 lg:py-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)] border border-gray-100/80"
          >
            {/* Dose circle */}
            <div
              className="w-10 h-10 lg:w-11 lg:h-11 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${ing.color}12`, border: `1.5px solid ${ing.color}30` }}
            >
              <span
                className="text-xs lg:text-sm font-bold"
                style={{ color: ing.color }}
              >
                {ing.dose}
              </span>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-xs lg:text-sm font-semibold text-gray-900 truncate">
                  {ing.name}
                </span>
                <span className="text-[9px] lg:text-[10px] font-mono text-gray-400 flex-shrink-0">
                  {ing.shortName}
                </span>
              </div>
              <p className="text-[10px] lg:text-xs text-gray-500 mt-0.5">
                {ing.benefit}
              </p>
            </div>

            {/* Check */}
            <svg className="w-4 h-4 lg:w-5 lg:h-5 text-green-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        ))}
      </div>

      {/* Footer badges */}
      <div className="px-4 pb-4 lg:px-6 lg:pb-5 flex items-center justify-center gap-3 lg:gap-4">
        <span className="text-[8px] lg:text-[9px] text-gray-400 font-medium uppercase tracking-wider flex items-center gap-1">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          No Parabens
        </span>
        <span className="text-gray-300">·</span>
        <span className="text-[8px] lg:text-[9px] text-gray-400 font-medium uppercase tracking-wider flex items-center gap-1">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          No Sulfates
        </span>
        <span className="text-gray-300">·</span>
        <span className="text-[8px] lg:text-[9px] text-gray-400 font-medium uppercase tracking-wider flex items-center gap-1">
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          GMP Certified
        </span>
      </div>
    </div>
  );
}

/** Thumbnail for the gallery strip */
export function IngredientsThumbnail() {
  return (
    <div className="w-full h-full bg-[#0A1628] flex flex-col items-center justify-center gap-0.5 p-1">
      <svg className="w-4 h-4 text-white/80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 3h6v11a3 3 0 01-6 0V3z" />
        <path d="M12 14v4" />
        <path d="M8 18h8" />
      </svg>
      <span className="text-[6px] font-bold text-white text-center leading-tight">WHAT'S<br/>INSIDE</span>
    </div>
  );
}
